/**
 * Linked chess-username cache (localStorage) — the single, synchronous source for "who is the
 * signed-in player on chess.com/lichess". Seeded by useChessUsername after the profile loads; read
 * on mount so pages know the player instantly, with no network/React-Query dependency.
 *
 * Extracted so both useChessUsername (the writer) and the game-import color resolver (a reader) share
 * ONE key + shape. The import path needs a synchronous read: the React Query userProfile cache isn't
 * guaranteed populated when a ?gameId= load runs, so relying on it dropped the perspective match and
 * defaulted to white (the ilan100 bug — a game where only black was coached opened on empty white).
 */
const CACHE_KEY = 'chess-deck-linked-username';

export interface LinkedUsername {
  readonly chessUsername: string;
  readonly platform: string;
}

export function readLinkedUsername(): LinkedUsername | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.chessUsername) return parsed as LinkedUsername;
    return null;
  } catch {
    return null;
  }
}

export function writeLinkedUsername(username: string, platform: string): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ chessUsername: username, platform }));
  } catch {
    /* localStorage full or unavailable */
  }
}

export function clearLinkedUsername(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}
